/*

  Usage example:

    const req = new Request({
      url: String [false], // => url to call
      async: Boolean [true], =>
      readystate: Function [Function]
    }).fetch().then(response => {
      // do something with the response
    }).catch(err => {
      // handle request errors
    });

*/
let parseResponse = (s: string) => {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
};

// transforms an object into FormData
let formify = (data = {}, fd = false) => {
  if (fd === false) {
    let e = [];
    for (let key in data) e.push(`${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`);
    return e.join("&").replace(/%20/g, "+");
  } else {
    // parse form data object
    let d = new FormData();
    for (let key in data) {
      if (data[key] instanceof Array) {
        data[key].forEach((v: string) => d.append(key, v));
      } else {
        d.append(key, data[key]);
      }
    }
    return d;
  }
};

export default class Request {
  props: {
    url: string;
    withCredentials: boolean;
    contentType: string;
    async: boolean;
    parseFormData: boolean;
  };

  constructor(props = {}) {
    this.props = {
      url: "/",
      withCredentials: false,
      contentType: "application/json",
      async: true,
      parseFormData: false,
      ...props,
    };
  }

  // Request.fetch will deprecate in favor of Request.get
  fetch() {
    return this.get();
  }

  // creates a GET request
  get() {
    let { url, withCredentials, async } = this.props;

    return new Promise((resolve, reject) => {
      let ajax = new XMLHttpRequest();
      ajax.onreadystatechange = function () {
        if (this.readyState == 4) {
          const res = parseResponse(this.responseText);
          switch (this.status) {
            case 404:
            case 500:
              return reject(res);
            case 200:
            default:
              return resolve(res);
          }
        }
      };

      ajax.open("GET", url, async);
      ajax.withCredentials = withCredentials;
      ajax.send();
    });
  }

  // creates a POST request
  post(data: any, string?: boolean) {
    return new Promise((resolve, reject) => {
      let { url, contentType, parseFormData } = this.props;

      const xhr = new XMLHttpRequest();

      xhr.onreadystatechange = function () {
        if (this.readyState === 4) {
          const res = parseResponse(this.responseText);
          switch (this.status) {
            case 404:
            case 500:
              return reject(res);
            case 200:
            default:
              return resolve(res);
          }
        }
      };

      xhr.open("POST", url, true);
      xhr.setRequestHeader("Content-Type", contentType);

      xhr.setRequestHeader("X-CSRF-Token", globalThis.csrf_token);

      xhr.withCredentials = true;

      if (contentType == "application/x-www-form-urlencoded") {
        data = formify(data, parseFormData);
      } else if (string === true) {
        data = JSON.stringify(data);
      }
      xhr.send(data);
    });
  }

  // creates a PUT request
  update(data: any, string: boolean) {
    return new Promise((resolve, reject) => {
      let { url, contentType, parseFormData } = this.props;

      const xhr = new XMLHttpRequest();

      xhr.onreadystatechange = function () {
        if (this.readyState === 4) {
          const res = parseResponse(this.responseText);
          switch (this.status) {
            case 404:
            case 500:
              return reject(res);
            case 200:
            default:
              return resolve(res);
          }
        }
      };
      xhr.open("PUT", url, true);
      xhr.setRequestHeader("Content-Type", contentType);
      xhr.setRequestHeader("X-CSRF-Token", globalThis.csrf_token);
      xhr.withCredentials = true;

      if (contentType == "application/x-www-form-urlencoded") {
        data = formify(data, parseFormData);
      } else if (string === true) {
        data = JSON.stringify(data);
      }

      xhr.send(data);
    });
  }

  // creates a DELETE request
  delete() {
    return new Promise((resolve, reject) => {
      let { url } = this.props;

      const xhr = new XMLHttpRequest();

      xhr.onreadystatechange = function () {
        if (this.readyState === 4) {
          switch (this.status) {
            case 404:
            case 500:
              return reject();
            case 200:
            default:
              return resolve(null);
          }
        }
      };

      xhr.open("DELETE", url, true);
      xhr.setRequestHeader("Content-Type", "application/x-www-form-urlencoded");
      xhr.withCredentials = true;
      xhr.setRequestHeader("X-CSRF-Token", globalThis.csrf_token);

      xhr.send();

      resolve(null);
    });
  }
}
